<template>
  <div ref="graphContainer" class="w-full h-full flex flex-col px-3 pt-3 pb-0.5">
    <div class="flex-grow w-full flex">
      <!-- Y-axis Labels -->
      <div class="h-full flex flex-col" :style="{width: `${MAX_Y_LABEL_WIDTH}px`}">
        <BaseText type="label" size="xs" class="text-text-muted" style="height: 22px">
          {{ getMetricLookup?.[scaleKpi]?.name || scaleKpi }}
        </BaseText>
        <div class="flex-grow flex flex-col justify-between">
          <div v-for="(label, index) in [...yAxisLabels].reverse()" :key="`y-axis-label-${index}`" class="fade-in">
            <BaseText type="body" size="xs" class="text-text-normal">
              {{ getIsPrivateMode ? '••' : label }}
            </BaseText>
          </div>
        </div>
      </div>
      <!-- Graph Render Area -->
      <div class="relative flex-grow h-full min-w-0" style="padding-bottom: 2px">
        <!-- Data Render Area -->
        <div class="w-full h-full absolute left-0 top-0" style="padding-top: 29px; padding-bottom: 8px; z-index: 100" 
        :style="{paddingLeft: `${dataAreaLeftPadding}px`, paddingRight: `${xAxisPadding}px`}">
          <div ref="dataArea" class="w-full h-full">
            <DataRenderer 
              ref="dataRenderer"
              :data="data"
              :axis-scales="axisScales"
              :performance-kpi="performanceKpi"
              :scale-kpi="scaleKpi"
              :data-area-dimensions="dataAreaDimensions"
              :is-active-tab="isActiveTab"
              @show-insights-drawer="$emit('show-insights-drawer', $event)"
            />
          </div>
        </div>
        <!-- Background Render Area -->
        <div class="w-full h-full">
          <BackgroundRenderer 
            ref="backgroundRenderer"
            :axis-scales="axisScales"
            :performance-threshold="performanceThreshold"
            :performance-direction="performanceDirection"
            :scale-threshold="scaleThreshold"
            :losing-threshold="losingThreshold"
            :data-area-padding="{ top: 29, bottom: 8 - 2, left: dataAreaLeftPadding, right: xAxisPadding }"
            :hovered-category="hoveredCategory"
          />
        </div>
      </div>
    </div>
    <!-- X-axis Labels -->
    <div class="w-full h-9 flex items-center">
      <!-- Performance Metric Label -->
      <div class="relative">
        <BaseText type="label" size="xs" class="text-text-muted truncate cursor-default" :style="{width: `${MAX_X_LABEL_WIDTH}px`}"
        @mouseenter.native="xAxisTitleHovered = true" @mouseleave.native="xAxisTitleHovered = false">
          {{ getMetricLookup?.[performanceKpi]?.name || performanceKpi }}
        </BaseText>
        <!-- Hover Tooltip -->
        <transition>
          <div v-if="xAxisTitleHovered" class="x-axis-title-tooltip px-2 py-1 rounded-md">
            <BaseText type="body" size="xs" class="text-white whitespace-nowrap">
              {{ getMetricLookup?.[performanceKpi]?.name || performanceKpi }}
            </BaseText>
          </div>
        </transition>
      </div>
      <div class="flex-grow flex items-center justify-between" :style="{ paddingLeft: `${xAxisPadding}px`, paddingRight: `${xAxisPadding}px` }">
        <div v-for="(label, index) in xAxisLabels" :key="`x-axis-label-${index}`" class="relative w-0">
          <BaseText type="body" size="xs" class="text-text-normal absolute -left-1/2 -top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            {{ getIsPrivateMode ? '••' : label }}
          </BaseText>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { scaleLinear } from 'd3-scale'
import formatLabelString from '../../../../utils/lens/formatGraphLabelString'

import DataRenderer from './DataRenderer.vue'
import BackgroundRenderer from './BackgroundRenderer.vue'

const MAX_Y_TICKS = 11 // int
const MIN_X_TICK_SPACING = 56 // px
const MAX_Y_LABEL_WIDTH = 52 // px
const MAX_X_LABEL_WIDTH = 68 // px

export default {
  name: 'CreativeTestScatterChart',
  components: {
    DataRenderer,
    BackgroundRenderer
  },
  props: {
    lens: {
      type: Object,
    },
    data: {
      type: Array,
      default: () => []
    },
    thresholds: {
      type: Object
    },
    maxValues: {
      type: Object,
      default: () => ({})
    },
    hoveredCategory: {
      type: String
    },
    isActiveTab: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      axisScales: [],
      xAxisLabels: [],
      yAxisLabels: [],
      MAX_X_LABEL_WIDTH,
      MAX_Y_LABEL_WIDTH,
      resizeObserver: null,
      dataAreaDimensions: { width: 0, height: 0 },
      xAxisTitleHovered: false,
    }
  },
  watch: {
    data: {
      handler () {
        this.recomputeChartRenderData()
      },
      deep: true
    },
    thresholds: {
      handler () {
        this.recomputeChartRenderData()
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricLookup']),
    ...mapGetters('MiscModule', ['getIsPrivateMode']),
    xAxisPadding () {
      return Math.floor(MIN_X_TICK_SPACING / 2)
    },
    dataAreaLeftPadding () {
      return this.MAX_X_LABEL_WIDTH - this.MAX_Y_LABEL_WIDTH + this.xAxisPadding
    },
    performanceKpi () {
      return this.thresholds?.winners?.key || 'roas'
    },
    scaleKpi () {
      return this.thresholds?.scaling?.key || 'spend'
    },
    performanceThreshold () {
      return this.thresholds?.winners?.value || 3
    },
    performanceDirection () {
      return this.thresholds?.winners?.operation === 'less_than' ? 'negative' : 'positive'
    },
    scaleThreshold () {
      return this.thresholds?.scaling?.value || 1500
    },
    losingThreshold () {
      return this.thresholds?.losers?.value || 300
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.initializeAxisInformation()
      this.computeDataAreaDimensions()
      if (this.$refs.graphContainer) {
        this.resizeObserver = new ResizeObserver(() => {
          this.computeXAxisLabels()
          this.computeDataAreaDimensions()
          this.$refs.backgroundRenderer.computeSectionGridDimensions()
          this.$refs.dataRenderer.computeDataPoints()
        })
        this.resizeObserver.observe(this.$refs.graphContainer)
      }
    })
  },
  beforeDestroy () {
    if (this.resizeObserver && this.$refs.graphContainer) {
      this.resizeObserver.unobserve(this.$refs.graphContainer)
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  },
  methods: {
    recomputeChartRenderData () {
      this.initializeAxisInformation()
      this.$refs.backgroundRenderer.computeSectionGridDimensions()
      this.$refs.dataRenderer.computeDataPoints()
    },
    initializeAxisInformation () {
      if (this.maxValues[this.performanceKpi] == null || this.maxValues[this.scaleKpi] == null) {
        this.axisScales = []
        this.xAxisLabels = []
        this.yAxisLabels = []
        return
      }
      this.computeAxisScales()
      this.computeXAxisLabels()
      this.computeYAxisLabels()
    },
    computeAxisScales () {
      const scales = []
      if (this.maxValues[this.performanceKpi] != null && this.maxValues[this.scaleKpi] != null) {
        const maxPerformanceValue = Math.max(this.maxValues[this.performanceKpi], this.performanceThreshold)
        const maxScaleValue = Math.max(this.maxValues[this.scaleKpi], this.scaleThreshold)
        scales.push(createKpiAxisScale(maxPerformanceValue))
        scales.push(createKpiAxisScale(maxScaleValue))
      }
      this.axisScales = scales
    },
    computeXAxisLabels () {
      // Compute the maximum number of labels we can render on the x-axis (depending on width)
      this.xAxisLabels = []
      let maxTicks = 10
      if (this.$refs.graphContainer) {
        const width = this.$refs.graphContainer.clientWidth
        if (!width) return // Don't compute labels if the container is not rendered yet (bit of a hack - resize triggers when we switch tabs due to keep-alive)
        maxTicks = Math.max(Math.floor(width / MIN_X_TICK_SPACING), 1)
      }
      const scale = this.axisScales[0]
      let ticks = scale.ticks(maxTicks)
      ticks = this.reduceLabelTicks(ticks, scale, maxTicks)
      this.xAxisLabels = ticks.map(tick => this.formatAxisLabelString(tick, 'performance'))
    },
    computeYAxisLabels () {
      const scale = this.axisScales[1]
      let ticks = scale.ticks(MAX_Y_TICKS)
      ticks = this.reduceLabelTicks(ticks, scale, MAX_Y_TICKS)
      this.yAxisLabels = ticks.map(tick => this.formatAxisLabelString(tick, 'scale'))
    },
    reduceLabelTicks (ticks, scale, maxTicks) {
      if (ticks.length <= maxTicks) return ticks
      let tickValues = [...ticks]
      if (ticks.length % 2 === 0) {
        const newMax = ticks[ticks.length - 1] + ticks[1]
        tickValues.push(newMax)
        scale.domain([0, newMax])
      }
      while (tickValues.length > maxTicks) {
        tickValues = tickValues.filter((_, index) => index % 2 === 0)
      }
      return tickValues
    },
    formatAxisLabelString (value, axis) {
      const kpi = axis === 'performance' ? this.performanceKpi : this.scaleKpi
      const type = this.getMetricLookup?.[kpi]?.type
      return formatLabelString(value, type, this.lens?.currency)
    },
    computeDataAreaDimensions () {
      if (!this.$refs.dataArea) return
      const areaBounds = this.$refs.dataArea
      this.dataAreaDimensions = {
        width: areaBounds.clientWidth,
        height: areaBounds.clientHeight
      }
    }
  }
}

const createKpiAxisScale = (maxValue) => {
  const max = maxValue || 1
  const scale = scaleLinear()
    .domain([0, max])
    .nice()
  return scale
}
</script>

<style scoped>
.x-axis-title-tooltip {
  position: absolute;
  bottom: calc(100% + 6px);
  left: -6px;
  z-index: 100;
  background-color: rgba(6, 7, 16, 0.88);
  backdrop-filter: blur(12px);
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>